import Image from "next/image";
import React, { useMemo, useState, useCallback } from "react";
import { useFloating, shift, autoUpdate, offset } from "@floating-ui/react-dom";
import { CSSTransition } from "react-transition-group";
import { mergeRefs } from "react-merge-refs";

import classNames from "classnames";
import Link from "next/link";

import Snippet from "assets/iconsv2/snippets-16.svg";

import profileOne from "assets/teams/profile-1.png";
import profileTwo from "assets/teams/profile-2.png";
import profileThree from "assets/teams/profile-3.png";
import profileFour from "assets/teams/profile-4.png";
import profileFive from "assets/teams/profile-5.png";
import profileSix from "assets/teams/profile-6.png";
import profileZeven from "assets/teams/profile-7.png";
import profileEight from "assets/teams/profile-8.png";
import profileNine from "assets/teams/profile-9.png";
import profileTen from "assets/teams/profile-10.png";
import profileEleven from "assets/teams/profile-11.png";

import CircleOne from "assets/teams/circle-1.svg";
import CircleTwo from "assets/teams/circle-2.svg";
import CircleThree from "assets/teams/circle-3.svg";

import ArrowRight from "assets/icons/arrow-right.svg";

import Chevron from "assets/iconsv2/chevron-right-16.svg";
import ComputerChip from "assets/iconsv2/computer-chip-16.svg";
import LinkIcon from "assets/iconsv2/link-16.svg";

import Button from "components/Button";

import styles from "./TeamsHeader.module.css";

type CircleAnchorPropTypes = {
  rotation: number;
  radius: number;
  direction: "left" | "right";
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
};

const CircleAnchor: React.FC<React.PropsWithChildren<CircleAnchorPropTypes>> = ({
  children,
  rotation,
  radius,
  direction = "left",
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <div
      className={classNames(styles.circleIconWrapper, {
        [styles.rotateLeft]: direction === "left",
        [styles.rotateRight]: direction === "right",
      })}
      style={{ width: `${radius}px`, "--rotation": `${rotation}deg` } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.circleIcon}>{children}</div>
    </div>
  );
};

const CircleAnchorPopover: React.FC<React.PropsWithChildren<{ popover: React.ReactElement }>> = ({
  children,
  popover,
}) => {
  const [mouseEntered, setMouseEntered] = useState(false);
  const {
    x,
    y,
    reference,
    floating: floatingReference,
    strategy,
  } = useFloating({
    whileElementsMounted: autoUpdate,
    middleware: [
      shift(),
      offset(({ rects: { floating, reference } }) => (floating.height / 2 + reference.height / 2) * -1),
    ],
  });

  const localPopoverRef = React.useRef(null);
  const popoverRef = useMemo(() => mergeRefs([floatingReference, localPopoverRef]), [floatingReference]);

  return (
    <div ref={reference} onMouseEnter={() => setMouseEntered(true)} onMouseLeave={() => setMouseEntered(false)}>
      <i style={{ opacity: mouseEntered ? 0 : 1 }}>{children}</i>
      <CSSTransition
        in={mouseEntered}
        timeout={300}
        unmountOnExit
        nodeRef={localPopoverRef}
        classNames={{
          enter: styles.popoverTransitionEnter,
          enterActive: styles.popoverTransitionEnterActive,
          exit: styles.popoverTransitionExit,
          exitActive: styles.popoverTransitionExitActive,
        }}
      >
        <div ref={popoverRef} style={{ position: strategy, top: y ?? 0, left: x ?? 0 }} className={styles.popover}>
          {popover}
        </div>
      </CSSTransition>
    </div>
  );
};

const TeamsHeader: React.FC = () => {
  const [isHovering, setHovering] = useState(false);

  const anchorMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const anchorMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);

  return (
    <>
      <div className={styles.TeamsHeader}>
        <div className={styles.circleContainer}>
          <div className={styles.circles}>
            <CircleOne />
            <CircleTwo />
            <CircleThree />
          </div>

          <div className={classNames(styles.circleAnchors, { [styles.withHoveredPopover]: isHovering })}>
            <CircleAnchor rotation={205} radius={382} direction="left">
              <Image src={profileOne} width={28} height={28} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={335} radius={382} direction="left">
              <Image src={profileTwo} width={21} height={21} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={25} radius={302} direction="right">
              <Image src={profileThree} width={21} height={21} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={315} radius={302} direction="right">
              <Image src={profileFour} width={21} height={21} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={145} radius={462} direction="right">
              <Image src={profileFive} width={24} height={24} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={145} radius={302} direction="right">
              <Image src={profileSix} width={21} height={21} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={110} radius={382} direction="left">
              <Image src={profileZeven} width={32} height={32} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={230} radius={462} direction="right">
              <Image src={profileEight} width={40} height={40} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={315} radius={462} direction="right">
              <Image src={profileNine} width={40} height={40} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={275} radius={382} direction="left">
              <Image src={profileTen} width={28} height={28} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>
            <CircleAnchor rotation={45} radius={382} direction="left">
              <Image src={profileEleven} width={32} height={32} layout="fixed" alt="photo of random team member" />
            </CircleAnchor>

            <CircleAnchor
              rotation={5}
              radius={462}
              direction="right"
              onMouseEnter={anchorMouseEnter}
              onMouseLeave={anchorMouseLeave}
            >
              <CircleAnchorPopover
                popover={
                  <>
                    <strong>Shared Quicklinks</strong>
                    <p>
                      Perfect for keeping your team on the same page, by converting long links into shared bookmarks.
                    </p>
                    <p>
                      Easily find your team&rsquo;s roadmaps, briefs, tools and more, whenever you need them. And help
                      new members onboard much faster.
                    </p>
                  </>
                }
              >
                <i className={styles.circleHoverTarget}>
                  <LinkIcon className="icon" />
                </i>
              </CircleAnchorPopover>
            </CircleAnchor>

            <CircleAnchor
              rotation={75}
              radius={462}
              direction="right"
              onMouseEnter={anchorMouseEnter}
              onMouseLeave={anchorMouseLeave}
            >
              <CircleAnchorPopover
                popover={
                  <>
                    <strong>Shared Commands</strong>
                    <p>
                      Use our APIs to build lists, grids and forms that connect to your tools. From admin panels to
                      waiting list automations - all in a few clicks.
                    </p>
                    <p>
                      Save time on everyday tasks and speed up your team&rsquo;s workflows. So they can work on the
                      stuff that matters most.
                    </p>
                  </>
                }
              >
                <i className={styles.circleHoverTarget}>
                  <ComputerChip className="icon" />
                </i>
              </CircleAnchorPopover>
            </CircleAnchor>

            <CircleAnchor
              rotation={185}
              radius={462}
              direction="right"
              onMouseEnter={anchorMouseEnter}
              onMouseLeave={anchorMouseLeave}
            >
              <CircleAnchorPopover
                popover={
                  <>
                    <strong>Shared Snippets</strong>
                    <p>Keep your team communicating efficiently by sharing snippets of text you use the most.</p>
                    <p>
                      Cut time and hassle with canned responses, coding and more. Use dynamic placeholders, keyword
                      expansion and other automations to write faster and better.
                    </p>
                  </>
                }
              >
                <i className={styles.circleHoverTarget}>
                  <Snippet className="icon" />
                </i>
              </CircleAnchorPopover>
            </CircleAnchor>
          </div>
        </div>
        <div className={styles.headerContent}>
          <a
            href="https://www.producthunt.com/posts/raycast-for-teams"
            target="_blank"
            rel="noreferrer"
            className={styles.pill}
          >
            Featured on Product Hunt
            <Chevron />
          </a>
          <h1>Your team, reimagined.</h1>
          <p>Take your team up a level with easy-to-use tools, effortless templates and efficient workflows.</p>
          <Link href="/teams/redirect" passHref>
            <Button renderAs="a">Get started today</Button>
          </Link>
          <a
            href="#learn-more"
            className={styles.learnMore}
            onClick={(event) => {
              event.preventDefault();
              document.querySelector("#learn-more")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Learn more <ArrowRight className={classNames("icon", styles.arrowDown)} />
          </a>
        </div>
        <div className={styles.headerBg}></div>
      </div>
    </>
  );
};

export default TeamsHeader;
